import { Td, Th, Tr, Thead, TableVirtual } from '@/common/ui/Table'
import { Button } from '@/common/ui/Form/Button'
import { useImportTable } from '../model'
import { useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Checkbox } from '@/common/ui/Form/Checkbox/checkbox'
import { ChoiceGroup } from '@/common/ui/ChoiceGroup'
import { PhotoPreview } from '@/common/ui/PhotoPreview'
import { useMeasure } from '@/common/hooks/useMeasure'
import { cn } from '@/common/lib/cn/cn'

const ROW_HEIGHT = 72

const IMPORT_COL_CHECKBOX_CLASS = 'w-13 min-w-13 max-w-13 box-border'
const IMPORT_COL_PHOTO_CLASS = 'w-14 min-w-14 max-w-14 box-border'

export type ImportTableFormProps = {
    onBack: () => void
    modalId: string
}

export const ImportTableForm = ({ onBack, modalId }: ImportTableFormProps) => {
    const {
        isLoading,
        flatProducts,
        integrations,
        integrationsReady,
        selectedMarketplace,
        setSelectedMarketplace,
        toggleProductSelection,
        handleSubmit,
        selectedProductIds,
        isSubmitting,
        submitError,
        isSubmitSuccess,
        pollingResult,
        taskId,
        resetImportState,
        onVisibleIndicesChange,
        isFetchingNextPage,
        listFetchErrorMessage,
    } = useImportTable()

    const [tableContainerRef, tableDimensions] = useMeasure()
    const tableHeight = Math.max(tableDimensions.height, 200)

    const isImportFlowPending =
        isSubmitting || Boolean(taskId && pollingResult?.state !== 'SUCCESS' && pollingResult?.state !== 'FAILURE')

    const handleRowClick = useCallback(
        (productId: string | number) => {
            if (isImportFlowPending) {
                return
            }
            toggleProductSelection(productId.toString())
        },
        [toggleProductSelection, isImportFlowPending]
    )

    const renderImportRow = useCallback(
        (index: number) => {
            const product = flatProducts[index]
            if (!product) {
                return (
                    <Tr aria-hidden>
                        <Td colSpan={7}> </Td>
                    </Tr>
                )
            }

            const selected =
                selectedProductIds.includes(product.marketplace_id?.toString() ?? '') || Boolean(product.is_exists)

            const rowDisabled = Boolean(product.is_exists) || isImportFlowPending

            return (
                <Tr
                    className={product.is_exists || isImportFlowPending ? undefined : 'cursor-pointer'}
                    onClick={() => handleRowClick(product.marketplace_id)}
                >
                    <Td className={IMPORT_COL_CHECKBOX_CLASS}>
                        <Checkbox
                            checked={selected}
                            onChange={() => handleRowClick(product.marketplace_id)}
                            disabled={rowDisabled}
                            onClick={e => e.stopPropagation()}
                        />
                    </Td>
                    <Td className={IMPORT_COL_PHOTO_CLASS}>
                        <PhotoPreview src={product.photos?.[0]?.small ?? undefined} alt={product.title || ''} />
                    </Td>
                    <Td>{product.title}</Td>
                    <Td>{product.brand}</Td>
                    <Td>{product.vendor_code}</Td>
                    <Td>{product.marketplace_id}</Td>
                    <Td>{product.category}</Td>
                </Tr>
            )
        },
        [flatProducts, selectedProductIds, handleRowClick, isImportFlowPending]
    )

    useEffect(() => {
        if (pollingResult?.state === 'SUCCESS') {
            const modal = document.getElementById(modalId) as HTMLDialogElement
            if (modal) {
                const input = document.getElementById(modalId) as HTMLInputElement
                input.click()
                setTimeout(() => {
                    resetImportState()
                }, 100)
            }
        }
    }, [pollingResult?.state, resetImportState, modalId])

    const showBlockingLoader = isLoading && flatProducts.length === 0

    const hasMarketplaceImportIntegration =
        integrations?.some(i => i.marketplace === 'wildberries' || i.marketplace === 'ozon') ?? false

    const integrationsHelpText = (
        <p className='shrink-0'>
            Добавлять и редактировать интеграции можно в <Link to='/app/profile/integrations'>Настройках</Link>
        </p>
    )

    return (
        <form
            className={cn(
                'flex min-h-0 flex-col gap-4',
                hasMarketplaceImportIntegration && 'h-[min(75vh,calc(100dvh-6rem))]'
            )}
            onSubmit={handleSubmit}
        >
            <h3>Импорт товаров из маркетплейсов</h3>
            {taskId && pollingResult?.state === 'FAILURE' && (
                <div className='alert alert-error' role='alert'>
                    <span>Ошибка при импорте: {pollingResult.traceback}</span>
                </div>
            )}
            {!integrationsReady ? (
                <div
                    className='flex min-h-[12rem] flex-1 items-center justify-center'
                    role='status'
                    aria-live='polite'
                    aria-label='Загрузка интеграций'
                >
                    <span className='loading loading-spinner loading-lg' />
                </div>
            ) : !hasMarketplaceImportIntegration ? (
                integrationsHelpText
            ) : (
                <>
                    <div
                        className={cn(
                            'flex shrink-0 flex-wrap items-center gap-3',
                            isImportFlowPending && 'pointer-events-none opacity-60'
                        )}
                    >
                        <ChoiceGroup
                            items={[
                                ...(integrations?.some(integration => integration.marketplace === 'wildberries')
                                    ? [{ label: 'Wildberries', iconName: 'wb' as const }]
                                    : []),
                                ...(integrations?.some(integration => integration.marketplace === 'ozon')
                                    ? [{ label: 'Ozon', iconName: 'ozon' as const }]
                                    : []),
                            ]}
                            activeIndex={
                                selectedMarketplace === 'ozon'
                                    ? integrations?.some(integration => integration.marketplace === 'wildberries')
                                        ? 1
                                        : 0
                                    : 0
                            }
                            onChange={index => {
                                const wildberriesAvailable = integrations?.some(
                                    integration => integration.marketplace === 'wildberries'
                                )
                                if (index === 0) {
                                    setSelectedMarketplace(wildberriesAvailable ? 'wildberries' : 'ozon')
                                    return
                                }
                                setSelectedMarketplace('ozon')
                            }}
                        />
                        <Button
                            as={Link}
                            to='/app/profile/integrations'
                            emphasis='secondary'
                            variant='ghost'
                            size='lg'
                            aria-label='Редактировать интеграции в настройках'
                        >
                            Редактировать интеграции
                        </Button>
                    </div>

                    {hasMarketplaceImportIntegration && (
                        <div className='relative flex min-h-0 flex-1 flex-col'>
                            <div ref={tableContainerRef} className='absolute inset-0' aria-hidden />
                            <div className='absolute inset-0 flex min-h-0 flex-col'>
                                {showBlockingLoader && (
                                    <div
                                        className='absolute inset-0 z-[1] flex items-center justify-center bg-[#27272e]/60'
                                        role='status'
                                        aria-live='polite'
                                        aria-label='Загрузка списка для импорта'
                                    >
                                        <span className='loading loading-spinner loading-lg' />
                                    </div>
                                )}
                                {isImportFlowPending && !showBlockingLoader && (
                                    <div
                                        className='absolute inset-0 z-[1] bg-[#27272e]/50'
                                        role='presentation'
                                        aria-hidden
                                    />
                                )}
                                <div className='relative flex min-h-0 flex-1 flex-col'>
                                    {listFetchErrorMessage && flatProducts.length > 0 && (
                                        <div className='alert alert-error mb-2 shrink-0' role='alert'>
                                            {listFetchErrorMessage}
                                        </div>
                                    )}
                                    <TableVirtual
                                        rowCount={flatProducts.length}
                                        rowHeight={ROW_HEIGHT}
                                        renderRow={renderImportRow}
                                        height={tableHeight}
                                        striped
                                        pinRows
                                        thBg
                                        onVisibleIndicesChange={onVisibleIndicesChange}
                                    >
                                        <Thead>
                                            <Tr>
                                                <Th className={IMPORT_COL_CHECKBOX_CLASS}>{''}</Th>
                                                <Th className={IMPORT_COL_PHOTO_CLASS}>Фото</Th>
                                                <Th>Название</Th>
                                                <Th>Бренд</Th>
                                                <Th>Артикул продавца</Th>
                                                <Th>Артикул МП</Th>
                                                <Th>Категория</Th>
                                            </Tr>
                                        </Thead>
                                    </TableVirtual>
                                    {!showBlockingLoader && flatProducts.length === 0 && listFetchErrorMessage && (
                                        <div
                                            className='pointer-events-none absolute inset-0 flex items-center justify-center px-4 pt-14'
                                            role='alert'
                                            aria-live='assertive'
                                        >
                                            <p className='pointer-events-auto max-w-lg text-center text-base text-error'>
                                                {listFetchErrorMessage}
                                            </p>
                                        </div>
                                    )}
                                    {!showBlockingLoader && flatProducts.length === 0 && !listFetchErrorMessage && (
                                        <div
                                            className='pointer-events-none absolute inset-0 flex items-center justify-center px-4 pt-14 text-center'
                                            role='status'
                                            aria-live='polite'
                                        >
                                            <p className='pointer-events-auto text-base text-[var(--gray-2,#aba7af)]'>
                                                Нет добавленных интеграций
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className='flex shrink-0 flex-col gap-2'>
                {submitError && (
                    <div className='alert alert-error'>
                        <span>Ошибка при импорте: {submitError.message}</span>
                    </div>
                )}
                {isSubmitSuccess && (
                    <div className='alert alert-success'>
                        <span>Товары успешно импортированы!</span>
                    </div>
                )}
                <div className='flex flex-wrap items-center justify-end gap-2'>
                    {isFetchingNextPage && flatProducts.length > 0 && (
                        <span
                            className='mr-auto inline-flex items-center gap-2 text-sm text-base-content/70'
                            role='status'
                            aria-live='polite'
                            aria-label='Подгрузка следующих позиций'
                        >
                            <span className='loading loading-spinner loading-xs' />
                            Загрузка…
                        </span>
                    )}
                    <Button emphasis='secondary' variant='ghost' size='lg' onClick={onBack}>
                        Отмена
                    </Button>
                    {hasMarketplaceImportIntegration && (
                        <Button
                            type='submit'
                            size='lg'
                            emphasis='accent'
                            disabled={
                                isImportFlowPending ||
                                (!isImportFlowPending &&
                                    (selectedProductIds.length === 0 ||
                                        !integrationsReady ||
                                        !hasMarketplaceImportIntegration))
                            }
                            loading={isImportFlowPending}
                            aria-busy={isImportFlowPending}
                        >
                            {isImportFlowPending
                                ? taskId
                                    ? 'Товары импортируются...'
                                    : 'Отправка...'
                                : 'Импортировать'}
                        </Button>
                    )}
                </div>
            </div>
        </form>
    )
}
